import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { shareInflightRequest } from '../../utils/share-inflight-request.util';
import { IProjectsService } from '../../interfaces/services/projects-service.interface';
import {
  Project,
  ProjectsContinuation,
  ProjectsDirection,
  ProjectsListPage,
  ProjectsQuery,
} from '../../interfaces/state/entities/project.interface';
import { environment } from '../../environments/environment';
import { RegisteredApplicationStore } from '../registered-applications/registered-application.store';

@Injectable({
  providedIn: 'root',
})
export class ProjectsService implements IProjectsService {
  private http = inject(HttpClient);
  private registeredApplicationStore = inject(RegisteredApplicationStore);
  private readonly projectsCollectionUrl = `${environment.MayaTestToolApiBaseUrl}/Projects`;
  private readonly inflightProjects = new Map<string, Observable<ProjectsListPage>>();

  getProjects(query: ProjectsQuery = {}): Observable<ProjectsListPage> {
    const direction: ProjectsDirection = query.direction ?? 'Descending';
    const bookmark = query.bookmark?.trim() ?? '';
    const key = `${this.clientKey()}|${direction}|${bookmark}`;
    return shareInflightRequest(this.inflightProjects, key, () => {
      let params = new HttpParams().set('Direction', direction);
      if (bookmark) {
        params = params.set('Bookmark', bookmark);
      }
      const headers = this.buildHeaders();
      return this.http
        .get<unknown>(this.projectsCollectionUrl, { headers, params })
        .pipe(map((response) => this.normalizePage(response)));
    });
  }

  addProject(name: string): Observable<Project> {
    return this.http.post<{ projectId: string }>(this.projectsCollectionUrl, {
      ProjectName: name
    }).pipe(
      map((response) => ({
        id: response.projectId,
        name,
        floorCount: 0,
        dateCreated: new Date(),
      }))
    );
  }

  deleteProject(id: string): Observable<void> {
    return this.http.delete<void>(`${this.projectsCollectionUrl}/${encodeURIComponent(id)}`);
  }

  private buildHeaders(): HttpHeaders | undefined {
    const clientId = this.registeredApplicationStore.selectedId();
    return clientId !== null && clientId.trim() !== ''
      ? new HttpHeaders({ 'x-ClientId': clientId })
      : undefined;
  }

  private clientKey(): string {
    return this.registeredApplicationStore.selectedId() ?? '';
  }

  private normalizePage(body: unknown): ProjectsListPage {
    return {
      projects: this.normalizeProjects(body),
      continuation: this.normalizeContinuation(body),
    };
  }

  private normalizeContinuation(body: unknown): ProjectsContinuation {
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
      return { hasMoreData: false };
    }

    const envelope = body as Record<string, unknown>;
    const raw = envelope['continuation'] ?? envelope['Continuation'];
    const continuation =
      raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
    const bookmark =
      continuation['bookmark'] ??
      continuation['Bookmark'] ??
      envelope['bookmark'] ??
      envelope['Bookmark'];
    const hasMoreData =
      continuation['hasMoreData'] ??
      continuation['HasMoreData'] ??
      continuation['hasMore'] ??
      continuation['HasMore'] ??
      envelope['hasMoreData'] ??
      envelope['HasMoreData'] ??
      envelope['hasMore'] ??
      envelope['HasMore'];
    const totalCount =
      continuation['totalCount'] ??
      continuation['TotalCount'] ??
      continuation['total'] ??
      continuation['Total'] ??
      envelope['totalCount'] ??
      envelope['TotalCount'] ??
      envelope['total'] ??
      envelope['Total'];
    const bookmarkText = bookmark == null ? '' : String(bookmark).trim();

    return {
      hasMoreData: this.readFlag(hasMoreData),
      bookmark: bookmarkText.length > 0 ? bookmarkText : undefined,
      totalCount: this.readCount(totalCount),
    };
  }

  /** Accepts JSON booleans and the string forms some gateways emit. */
  private readFlag(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    if (typeof value === 'string') {
      const text = value.trim().toLowerCase();
      return text === 'true' || text === '1';
    }
    return false;
  }

  /** Accepts a JSON number or a numeric string. Dev sometimes sends TotalCount as a string. */
  private readCount(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private normalizeProjects(body: unknown): Project[] {
    let rawList: unknown[] = [];

    if (Array.isArray(body)) {
      rawList = body;
    } else if (body && typeof body === 'object') {
      const o = body as Record<string, unknown>;
      if (Array.isArray(o['projects'])) {
        rawList = o['projects'] as unknown[];
      } else if (Array.isArray(o['Projects'])) {
        rawList = o['Projects'] as unknown[];
      } else if (Array.isArray(o['items'])) {
        rawList = o['items'] as unknown[];
      } else if (Array.isArray(o['Items'])) {
        rawList = o['Items'] as unknown[];
      }
    }

    return rawList
      .map((item) => this.mapProject(item))
      .filter((project): project is Project => project !== null);
  }

  private mapProject(item: unknown): Project | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const p = item as Record<string, unknown>;
    const id = p['projectId'] ?? p['ProjectId'] ?? p['id'] ?? p['Id'];
    if (id == null || String(id).trim() === '') {
      return null;
    }

    const rawName = p['projectName'] ?? p['ProjectName'] ?? p['name'] ?? p['Name'];
    const trimmedName = rawName != null ? String(rawName).trim() : '';
    const idString = String(id).trim();
    const name = trimmedName.length > 0 ? trimmedName : idString;

    const floorCount = p['floorCount'] ?? p['FloorCount'];
    const createdAt = p['createdAt'] ?? p['CreatedAt'];

    return {
      id: idString,
      name,
      floorCount: typeof floorCount === 'number' ? floorCount : Number(floorCount ?? 0),
      dateCreated: createdAt != null ? new Date(String(createdAt)) : new Date(),
    };
  }
}
